/* eslint-disable @typescript-eslint/no-explicit-any */
import type { ActionTree } from "vuex/types/index.js";

interface CartTransaction {
  count: number;
  subtotal: number;
  tax: number;
  total: number;
  ivaPercentage: number;
  pickupStoreId: number;
  shipping: number;
  shippingDiscount: number;
  shippingInsurance: number;
  shippingMinAmount: number;
  shippingRequired: boolean;
  totalDiscount: number;
  promotionDiscount: number;
  couponDiscount: number;
  hasCoupon: boolean;
  couponCode: string;
  hasShipping: boolean;
  hasShippingInsurance: boolean;
  insurancePerThousand: number;
  walletPromotionAmount: number;
  walletPromotionDetails: Array<any>;
  shippingPromotionId: number;
  shippingPromotionProducts: Array<any>;
  pickupPerson: any;
  creditToUse: number;
  walletToUse: number;
  totalWithCreditAndWallet: number;
  maxWalletAmount: number;
  monthlyPaymentGroups: Array<any>;
  paymentApplied: number;
}

export interface StateTransaction {
  version?: number;
  totalItems: number;
  items: StateLocalProductInCart[];
  cart: CartTransaction;
}

interface StateLocalProductInCart {
  productId: number;
  qty: number;
}

function getCalculatedShippingInsurance(state: StateTransaction) {
  const total =
    (state.cart.subtotal - state.cart.promotionDiscount + state.cart.shipping) *
    (1 + state.cart.ivaPercentage / 100);
  return Math.ceil(total / 1000) * +state.cart.insurancePerThousand;
}

export const defaultCartInit = {
  version: 1.1,
  totalItems: 0,
  items: [],

  cart: {
    count: 0,
    subtotal: 0,
    tax: 0,
    total: 0,
    ivaPercentage: 0,
    pickupStoreId: 0,
    shipping: 0,
    shippingInsurance: 0,
    shippingDiscount: 0,
    shippingMinAmount: 0,
    shippingRequired: false,
    totalDiscount: 0,
    promotionDiscount: 0,
    couponDiscount: 0,
    hasCoupon: false,
    couponCode: "",
    hasShipping: false,
    hasShippingInsurance: false,
    insurancePerThousand: 0,
    walletPromotionAmount: 0,
    walletPromotionDetails: [],
    shippingPromotionId: 0,
    shippingPromotionProducts: [],
    pickupPerson: null,
    creditToUse: 0,
    walletToUse: 0,
    maxWalletAmount: 0,
    totalWithCreditAndWallet: 0,
    monthlyPaymentGroups: [],
    paymentApplied: 0,
  },
};

const state = () => defaultCartInit;

// mutations
const mutations = {
  restProductInCart(state: StateTransaction) {
    state.items = [];
  },
  setItems(state: StateTransaction, items: Array<any>) {
    state.items = items;
  },

  removeProductInLocal(state: StateTransaction, productId: number) {
    const index = state.items.findIndex((item) => item.productId === productId);
    if (index !== -1 && state.items[index]) {
      state.totalItems -= state.items[index].qty;
      state.items.splice(index, 1);
    }
  },

  addProductTotalItems(state: StateTransaction, qty: number) {
    state.totalItems = state.totalItems + qty;
  },

  setCart(state: StateTransaction, cart: CartTransaction) {
    state.cart = cart;
  },
  setCartFormatDB(state: StateTransaction, data: any) {
    const cart: CartTransaction = {
      count: data?.count ?? 0,
      total: +data?.total * 1,
      subtotal: data?.subtotal,
      tax: data?.tax,
      ivaPercentage: data?.iva_percentage,
      pickupStoreId: data?.pickup_store_id,
      shipping: data?.shipping,
      shippingInsurance: data?.shipping_insurance,
      shippingDiscount: data?.shipping_discount,
      shippingMinAmount: data?.shipping_min_amount,
      shippingRequired: data?.shipping_required,
      totalDiscount: data?.total_discount,
      promotionDiscount: data?.promotion_discount,
      couponDiscount: data?.coupon_discount,
      hasCoupon: data?.has_coupon,
      couponCode: data?.coupon_code,
      hasShipping: data?.has_shipping,
      hasShippingInsurance: data?.has_shipping_insurance,
      insurancePerThousand: data?.insurance_per_thousand,
      walletPromotionAmount: data?.wallet_promotion_amount,
      walletPromotionDetails: data?.wallet_promotion_details,
      shippingPromotionId: data?.shipping_promotion_id,
      shippingPromotionProducts: data?.shipping_promotion_products,
      pickupPerson: data?.pickup_person,
      creditToUse: data?.credit_to_use,
      walletToUse: data?.wallet_to_use,
      maxWalletAmount: data?.max_wallet_amount,
      totalWithCreditAndWallet: data?.total_with_credit_and_wallet,
      monthlyPaymentGroups: data?.monthlyPaymentGroups?.groups, //  [],
      paymentApplied: data.payment_applied ? data.payment_applied : 0,
    };
    state.cart = cart;
  },
  resetCart(state: StateTransaction) {
    state.cart = {
      count: 0,
      subtotal: 0,
      tax: 0,
      total: 0,
      ivaPercentage: 0,
      pickupStoreId: 0,
      shipping: 0,
      shippingInsurance: 0,
      shippingMinAmount: 0,
      shippingRequired: false,
      shippingDiscount: 0,
      totalDiscount: 0,
      promotionDiscount: 0,
      couponDiscount: 0,
      hasCoupon: false,
      couponCode: "",
      hasShipping: false,
      hasShippingInsurance: false,
      walletPromotionAmount: 0,
      walletPromotionDetails: [],
      insurancePerThousand: 0,
      shippingPromotionId: 0,
      shippingPromotionProducts: [],
      pickupPerson: null,
      creditToUse: 0,
      walletToUse: 0,
      maxWalletAmount: 0,
      totalWithCreditAndWallet: 0,
      monthlyPaymentGroups: [],
      paymentApplied: 0,
    };
    state.items = [];
    state.totalItems = 0;
  },
  setSubtotal(state: StateTransaction, newSubtotal: number) {
    const oldSubtotal = state.cart.subtotal;
    const oldSubtotalTax =
      +((oldSubtotal * state.cart.ivaPercentage) / 100).toFixed(2) * 1;
    const newSubtotalTax =
      +((newSubtotal * state.cart.ivaPercentage) / 100).toFixed(2) * 1;
    state.cart.subtotal = +newSubtotal.toFixed(2) * 1;
    state.cart.tax =
      +(state.cart.tax - oldSubtotalTax + newSubtotalTax).toFixed(2) * 1;
    state.cart.total =
      +(
        state.cart.total -
        oldSubtotal -
        oldSubtotalTax +
        newSubtotal +
        newSubtotalTax
      ).toFixed(2) * 1;
    state.cart.totalWithCreditAndWallet =
      +(
        state.cart.total -
        state.cart.walletToUse -
        state.cart.paymentApplied
      ).toFixed(2) * 1;
  },
  calcShippingInsurance(state: StateTransaction, forceInsurance: any) {
    const force = forceInsurance !== undefined && forceInsurance !== null;
    if (state.cart.shippingInsurance <= 0 && !force) {
      return;
    }
    const newInsuranceValue = force
      ? forceInsurance
      : getCalculatedShippingInsurance(state);
    const newInsuranceTax =
      (newInsuranceValue * state.cart.ivaPercentage) / 100;
    const oldInsuranceTax =
      (state.cart.shippingInsurance * state.cart.ivaPercentage) / 100;
    const newInsuranceWithTax = newInsuranceValue + newInsuranceTax;
    const oldInsuranceWithTax = state.cart.shippingInsurance + oldInsuranceTax;
    state.cart.total =
      state.cart.total - oldInsuranceWithTax + newInsuranceWithTax;
    state.cart.tax = state.cart.tax - oldInsuranceTax + newInsuranceTax;
    state.cart.shippingInsurance = newInsuranceValue;
    state.cart.hasShippingInsurance = newInsuranceValue > 0;
    state.cart.totalWithCreditAndWallet =
      +(
        state.cart.total -
        state.cart.walletToUse -
        state.cart.paymentApplied
      ).toFixed(2) * 1;
  },
  setShipping(state: StateTransaction, newShipping: number) {
    const taxtRatio = state.cart.ivaPercentage / 100;
    const oldShippingTax = state.cart.shipping * taxtRatio;
    const newShippingTax = newShipping * taxtRatio;
    const oldShippingWithTaxt = state.cart.shipping + oldShippingTax;
    const newShippingWithTaxt = newShipping + newShippingTax;
    state.cart.total =
      +(state.cart.total - oldShippingWithTaxt + newShippingWithTaxt).toFixed(
        2,
      ) * 1;
    state.cart.tax =
      +(state.cart.tax - oldShippingTax + newShippingTax).toFixed(2) * 1;
    state.cart.shipping = +newShipping.toFixed(2) * 1;
    state.cart.hasShipping = true;
    state.cart.totalWithCreditAndWallet =
      state.cart.total - state.cart.walletToUse - state.cart.paymentApplied;
  },
  setMonthlyPaymentGroups(state: StateTransaction, groups: Array<any>) {
    state.cart.monthlyPaymentGroups = groups;
  },
  setCreditToUse(state: StateTransaction, creditToUse: number) {
    state.cart.creditToUse = creditToUse;
  },
  setWalletToUse(state: StateTransaction, walletToUse: number) {
    state.cart.walletToUse = walletToUse;
  },
  setShippingInsurance(state: StateTransaction, newshippingInsurance: number) {
    const taxtRatio = state.cart.ivaPercentage / 100;
    const oldShippingInsuranceTax = state.cart.shippingInsurance * taxtRatio;
    const newShippingInsuranceTax = newshippingInsurance * taxtRatio;
    const oldShippingInsuranceWithTaxt =
      state.cart.shippingInsurance + oldShippingInsuranceTax;
    const newShippingInsuranceWithTaxt =
      newshippingInsurance + newShippingInsuranceTax;
    state.cart.total =
      +(
        state.cart.total -
        oldShippingInsuranceWithTaxt +
        newShippingInsuranceWithTaxt
      ).toFixed(2) * 1;
    state.cart.tax =
      +(
        state.cart.tax -
        oldShippingInsuranceTax +
        newShippingInsuranceTax
      ).toFixed(2) * 1;
    state.cart.shippingInsurance = +newshippingInsurance.toFixed(2) * 1;
    if (newshippingInsurance > 0) {
      state.cart.hasShippingInsurance = true;
    } else {
      state.cart.hasShippingInsurance = false;
    }
    state.cart.totalWithCreditAndWallet =
      state.cart.total - state.cart.walletToUse - state.cart.paymentApplied;
  },
  setTotalItems(state: StateTransaction, totalItems: number) {
    state.totalItems = totalItems;
  },
};

const getters = {
  getTotalItems(state: StateTransaction) {
    return state.totalItems;
  },
  getItems(state: StateTransaction) {
    return state.items;
  },
  getCart(state: StateTransaction) {
    return state.cart;
  },
  getCount(state: StateTransaction) {
    return state.cart.count;
  },
  getSubtotal(state: StateTransaction) {
    return state.cart.subtotal;
  },
  getTax(state: StateTransaction) {
    return state.cart.tax;
  },
  getTotal(state: StateTransaction) {
    return state.cart.total;
  },
  getIvaPercentage(state: StateTransaction) {
    return state.cart.ivaPercentage;
  },
  getPickupStoreId(state: StateTransaction) {
    return state.cart.pickupStoreId;
  },
  getShipping(state: StateTransaction) {
    return state.cart.shipping;
  },
  getShippingInsurance(state: StateTransaction) {
    return state.cart.shippingInsurance;
  },
  getShippingMinAmount(state: StateTransaction) {
    return state.cart.shippingMinAmount;
  },
  getTotalDiscount(state: StateTransaction) {
    return state.cart.totalDiscount;
  },
  getPromotionDiscount(state: StateTransaction) {
    return state.cart.promotionDiscount;
  },
  getCouponDiscount(state: StateTransaction) {
    return state.cart.couponDiscount;
  },
  getHasCoupon(state: StateTransaction) {
    return state.cart.hasCoupon;
  },
  getCouponCode(state: StateTransaction) {
    return state.cart.couponCode;
  },
  calculatedInsuranceCost(state: StateTransaction) {
    return getCalculatedShippingInsurance(state);
  },
  getMonthlyPaymentGroups(state: StateTransaction) {
    return state.cart.monthlyPaymentGroups;
  },
  getWalletPromotionAmount(state: StateTransaction) {
    return state.cart.walletPromotionAmount;
  },
  getWalletToUse(state: StateTransaction) {
    return state.cart.walletToUse;
  },
};

const actions: ActionTree<StateTransaction, any> = {
  addProductsInCart({ commit }, products: Array<unknown>) {
    products.forEach((product: unknown) => {
      commit("addProductInCart", product);
    });
  },
  setSubtotalAndRecalcInsurance({ commit }, newSubtotal) {
    commit("setSubtotal", newSubtotal);
    commit("calcShippingInsurance");
  },
  setShipping({ commit }, newShipping) {
    commit("setShipping", newShipping);
  },
  addProductIdInLocal({ state, commit }, product: StateLocalProductInCart) {
    const productFind = state.items.find((item: StateLocalProductInCart) => {
      return item.productId == product.productId;
    });
    if (productFind != undefined) {
      productFind.qty = productFind.qty + product.qty;
    } else {
      state.items.push({
        productId: product.productId,
        qty: product.qty,
      });
    }
    const totalItems = state.totalItems + product.qty;
    commit("setTotalItems", totalItems);
  },
};

export const transaction = {
  namespaced: true,
  state,
  mutations,
  getters,
  actions,
};
